import { apiStatic, requestError, requestSuccess } from 'AurionCR/components';
import { parseMixins, saveMixins } from 'AurionCR/components/formV2';
import { notifyRequestResult } from 'AurionCR/store/modules/notify';
import { getIsConflictOfInterests, prepareRequest, queryEncoderObject } from 'components/helpers';
import { pick } from 'lodash';
import { call, put, select, takeEvery, takeLatest } from 'redux-saga/effects';
import { API_RATING_PROCESS_STEP_COMMITTEE_USER } from 'services/rating-process-step-committee-user';
import {
  ballotAwareRequestError,
  invalidateCommitteeBallot,
} from 'services/rating-process-step-committee-matrix-ballot';
import {
  iRPState,
  iRPStepType3ActionTypes,
  iRPStepType3UserDeleteItemAction,
  iRPStepType3UserInitAction,
  iRPStepType3UserItem,
  iRPStepType3UserRefreshAction,
  iRPStepType3UserSaveItemAction,
} from '../../@type';
import {
  iRPStepType3SeriesRatingRefresh,
  iRPStepType3UserDeleteItemData,
  iRPStepType3UserMerge,
  iRPStepType3UserMergeItem,
  iRPStepType3UserMergeItemData,
  iRPStepType3UserRatEntVotRefresh,
  iRPStepType3UserRefresh,
} from '../../helpers';
import { onUpdateHtmlField } from 'utils/html-upload-helpers';

const ALLOWED_PATCH_COMMITTEE_USER_FIELDS = [
  'userID',
  'committeePositionID',
  'votingOrder',
  'isAllowedToVote',
  'isConflictOfInterests',
  'conflictOfInterestsInfo',
  'isNotConflictOfInterests',
] as const;
const ALLOWED_POST_COMMITTEE_USER_FIELDS = [...ALLOWED_PATCH_COMMITTEE_USER_FIELDS, 'ratingProcessStepCommitteeID'] as const;

const getData = (id: number) => {
  return apiStatic
    .post(
      API_RATING_PROCESS_STEP_COMMITTEE_USER.GET_ALL_DYNAMIC,
      queryEncoderObject({
        filter: `ratingProcessStepCommitteeID==${id}`,
        select: [
          'id',
          'ratingProcessStepCommitteeID',
          'userID',
          'user.fullName as userFullName',
          'committeePositionID',
          'committeePosition.title as committeePositionTitle',
          'votingOrder',
          'isAllowedToVote',
          'isConflictOfInterests',
          'conflictOfInterestsInfo',
        ].join(),
        orderBy: 'votingOrder',
      }),
    )
    .then((response) => {
      response.data.value = response.data.value.map((item: any) => ({
        ...item,
        isConflictOfInterests: getIsConflictOfInterests(item.isConflictOfInterests),
      }));
      return response;
    });
};

/**
 * F-19 — a saved roster change re-runs the member import on the backend (committee with a matrix
 * and imported members): refresh the voting summary the way ייבוא חברי ועדה does — it reloads the
 * ballot when done — or else only the ballot.
 */
function* afterRosterChange(committeeIndex: number) {
  const {
    stepType3: {
      list: {
        [committeeIndex]: {
          userRatEntVot: { isInit },
        },
      },
    },
  }: iRPState = yield select((state) => state.RP);
  if (isInit) {
    yield put(iRPStepType3UserRatEntVotRefresh({ committeeIndex }));
    yield put(iRPStepType3SeriesRatingRefresh({ committeeIndex }));
  } else {
    yield put(invalidateCommitteeBallot());
  }
}

export function* refresh({ payload: { committeeIndex } }: iRPStepType3UserRefreshAction) {
  const {
    stepType3: {
      list: {
        [committeeIndex]: {
          id,
          materials: { isLoading },
        },
      },
    },
  }: iRPState = yield select((state) => state.RP);
  if (!isLoading) {
    yield put(iRPStepType3UserMerge({ committeeIndex, isLoading: true }));
    try {
      const {
        data: { value },
      }: { data: { value: iRPStepType3UserItem['data'][] } } = yield call(getData, id);
      // set data
      yield put(
        iRPStepType3UserMerge({
          committeeIndex,
          isLoading: false,
          isInit: true,
          isInValid: value.length === 0,
          list: value.map((item, index) => ({
            index,
            isLoading: false,
            data: item,
          })),
        }),
      );
      // F-11 — a roster change can close the ballot gate until the next import.
      yield put(invalidateCommitteeBallot());
    } catch (e) {
      yield put(iRPStepType3UserMerge({ committeeIndex, isLoading: false }));
      yield put(notifyRequestResult(requestError(e), 'error'));
    }
  }
}

export function* init({ payload: { committeeIndex } }: iRPStepType3UserInitAction) {
  const {
    stepType3: {
      list: {
        [committeeIndex]: {
          user: { isInit },
        },
      },
    },
  }: iRPState = yield select((state) => state.RP);
  if (!isInit) {
    yield call(refresh, {
      payload: { committeeIndex },
      type: iRPStepType3ActionTypes.RP_STEP_TYPE_3_USER_REFRESH,
    });
  }
}

function* saveItem({
  payload: { committeeIndex, isReloadGrid, ...formData },
}: iRPStepType3UserSaveItemAction) {
  const {
    stepType3: {
      list: {
        [committeeIndex]: {
          id: ratingProcessStepCommitteeID,
          user: { list },
        },
      },
    },
  }: iRPState = yield select((state) => state.RP);
  const current = list.find((item) => item.data.id === formData.id);
  // status true
  if (isReloadGrid) {
    yield put(iRPStepType3UserMerge({ committeeIndex, isLoading: true }));
  }
  if (current) {
    yield put(
      iRPStepType3UserMergeItem({ committeeIndex, itemIndex: current.index, isLoading: true }),
    );
  }
  try {
    // @ts-ignore
    const dataWithHtml = yield onUpdateHtmlField({
      formData,
      field: 'conflictOfInterestsInfo',
    });
    let { data, mixins } = parseMixins(dataWithHtml);
    // @ts-ignore
    data = yield call(saveMixins, data, mixins);
    if (current) {
      const filteredData = pick(data, ALLOWED_PATCH_COMMITTEE_USER_FIELDS);
      yield call(
        apiStatic.patch,
        API_RATING_PROCESS_STEP_COMMITTEE_USER.PATCH(data),
        prepareRequest(filteredData),
      );
    } else {
      const filteredData = pick({ ...data, ratingProcessStepCommitteeID }, ALLOWED_POST_COMMITTEE_USER_FIELDS);
      yield call(
        apiStatic.post,
        API_RATING_PROCESS_STEP_COMMITTEE_USER.POST,
        prepareRequest(filteredData),
      );
    }
    if (current) {
      yield put(
        iRPStepType3UserMergeItemData({ committeeIndex, itemIndex: current.index, ...data }),
      );
    }
    // F-11 — eligibility (vote, position, conflict) feeds the ballot gate.
    yield call(afterRosterChange, committeeIndex);
    yield put(notifyRequestResult(requestSuccess('')));
  } catch (e) {
    if (current) {
      yield put(
        iRPStepType3UserMergeItemData({
          committeeIndex,
          itemIndex: current.index,
          ...current.data,
        }),
      );
    }
    // F-19 — committee-chair-locked keeps the old values; on general-error nothing was saved.
    yield put(notifyRequestResult(ballotAwareRequestError(e), 'error'));
    if (!isReloadGrid) {
      yield put(iRPStepType3UserRefresh({ committeeIndex }));
    }
  }
  // status false
  if (current) {
    yield put(
      iRPStepType3UserMergeItem({ committeeIndex, itemIndex: current.index, isLoading: false }),
    );
  }
  if (isReloadGrid) {
    yield put(iRPStepType3UserMerge({ committeeIndex, isEdit: null, isLoading: false }));
    yield put(iRPStepType3UserRefresh({ committeeIndex }));
  } else if (!current) {
    yield put(iRPStepType3UserMerge({ committeeIndex, isLoading: false }));
  }
}

function* deleteItem({ payload: { committeeIndex } }: iRPStepType3UserDeleteItemAction) {
  const {
    stepType3: {
      list: {
        [committeeIndex]: {
          user: { isDelete, list },
        },
      },
    },
  }: iRPState = yield select((state) => state.RP);
  const itemIndex = list.findIndex((item) => item.data.id === isDelete);
  if (itemIndex !== -1) {
    yield put(
      iRPStepType3UserMerge({ committeeIndex, isDelete: null, isEdit: null, isLoading: true }),
    );
    try {
      yield call(
        apiStatic.delete,
        API_RATING_PROCESS_STEP_COMMITTEE_USER.DELETE({ id: Number(isDelete) }),
      );
      yield put(iRPStepType3UserDeleteItemData({ committeeIndex, itemIndex }));
      yield put(iRPStepType3UserMerge({ committeeIndex, isInValid: list.length === 1 }));
      yield call(afterRosterChange, committeeIndex);
      yield put(notifyRequestResult(requestSuccess('')));
    } catch (e) {
      yield put(notifyRequestResult(ballotAwareRequestError(e), 'error'));
      yield put(iRPStepType3UserMerge({ committeeIndex, isLoading: false }));
      yield put(iRPStepType3UserRefresh({ committeeIndex }));
      return;
    }
    yield put(iRPStepType3UserMerge({ committeeIndex, isLoading: false }));
  }
}

export const User = [
  takeLatest(iRPStepType3ActionTypes.RP_STEP_TYPE_3_USER_INIT, init),
  takeLatest(iRPStepType3ActionTypes.RP_STEP_TYPE_3_USER_REFRESH, refresh),
  takeEvery(iRPStepType3ActionTypes.RP_STEP_TYPE_3_USER_SAVE_ITEM, saveItem),
  takeLatest(iRPStepType3ActionTypes.RP_STEP_TYPE_3_USER_DELETE_ITEM, deleteItem),
];
